// Locale-aware accessors for event copy, with a safe fallback to the raw feed
// text so unmigrated or partially-failed rows still render. Every consumer that
// shows an event's title / description / meta summary should read through these
// rather than touching `event.title` / `event.description` directly.
//
// The localized copy is produced by the translate-events Edge Function; see
// docs/event-translation.md §5.

import type { Locale } from './locales';
import type { DbEvent } from '../supabase';

/** Localized title, falling back to the original (French) feed title. */
export function localizedTitle(event: DbEvent, locale: Locale): string {
  return event.translations?.[locale]?.title ?? event.title;
}

/**
 * Localized search-result title for the page `<title>` — short, keyword-first,
 * generated separately so it isn't a truncation of the full title (which stays
 * in the <h1>). Falls back to the localized title.
 */
export function localizedSeoTitle(event: DbEvent, locale: Locale): string {
  return event.translations?.[locale]?.seoTitle || localizedTitle(event, locale);
}

/** Localized body description, falling back to the raw feed description. */
export function localizedDescription(event: DbEvent, locale: Locale): string | null {
  return event.translations?.[locale]?.description ?? event.description ?? null;
}

/**
 * Localized ≤160-char SEO meta summary. No feed fallback — the source has no
 * equivalent field, so callers should fall back to a truncated description.
 */
export function localizedSummary(event: DbEvent, locale: Locale): string | null {
  return event.translations?.[locale]?.summary ?? null;
}
